import styled from '@emotion/styled';
import { fit, theme, shouldForwardProp } from '@landsbankinn/ui';
import type { ImageLoaderProps } from 'next/image';
import Image from 'next/image';
import type { FC } from 'react';
import React, { forwardRef, useRef } from 'react';
import { animated } from 'react-spring';

export const FitImage = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ aspectRatio: number }>`
  position: relative;
  overflow: hidden;
  width: 100%;

  &:after {
    display: block;
    content: '';
    padding-top: ${(props) => (1 / props.aspectRatio) * 100}%;
  }

  img:first-child {
    ${fit}

    width: 100%;
    height: 100%;
    object-fit: cover;

    transform: translateZ(0px);
    backface-visibility: hidden;

    transition: 265ms ease-out;
    transition-property: transform, opacity;
  }
  > img.lazyloaded:not(.ls-is-cached):first-child {
    transition-property: transform, opacity, filter;
  }
  > img.ls-is-cached:first-child {
    transition-property: transform, opacity;
  }
`;

const OverflowHidden = styled(animated.div)`
  position: relative;
  overflow: hidden;
`;

const Svg = styled.div`
  img {
    margin: 0 auto;
  }
`;

const RatioMap = {
  ar_16_9: 16 / 9,
  ar_4_3: 4 / 3,
  ar_1_1: 1 / 1,
  aside: 1 / 1,
  card: 1.527,
};

export type ImgSource =
  | string
  | {
      url: string;
      base64?: string;
      alt?: string;
      dimensions?: { width: number; height: number };
      ar_1_1?: any;
      [key: string]: ImgSource | any;
    };

export type ImgAr =
  | 'ar_16_9'
  | 'ar_4_3'
  | 'ar_1_1'
  | 'ar_9_16'
  | 'aside'
  | 'card';

interface IProps {
  src?: ImgSource;
  alt?: string;
  isFit?: boolean;
  sizes?: string;
  percentage?: number;
  aspectRatio?: number;
  className?: string;
  ar?: ImgAr;
  onLoad?: any;
  containerStyle?: any;
  isSvg?: boolean;
  [key: string]: any;
}

function normalizeSrc(src: string): string {
  return src[0] === '/' ? src.slice(1) : src;
}

const imgixLoader = ({ src, width, quality }: ImageLoaderProps) => {
  const rect = src.match(/rect=((\d+,?){4})/)?.[0];
  const params = ['auto=format,compress', 'fit=max', 'w=' + width, rect];
  let paramsString = '';

  if (quality) {
    params.push('q=' + quality);
  }

  if (params.length) {
    paramsString = '?' + params.join('&');
  }
  return `${normalizeSrc(src.replace(/\?.*$/, ''))}${paramsString}`;
};

// @todo add <noscript>
export const Img: FC<IProps> = forwardRef(function InnerImg(
  {
    src,
    alt = '',
    isFit = true,
    percentage = 1,
    aspectRatio,
    ar = 'ar_16_9',
    containerStyle = {},
    isSvg,
    ...rest
  }: IProps,
  originRef,
) {
  const backupRef = useRef<HTMLImageElement>();
  const ref = originRef || backupRef;

  const sizes =
    rest.sizes ??
    `(min-width: ${theme.breakpoints.md.width}px), ${Math.ceil(
      theme.pageWidth * percentage,
    )}px, 100vw`;

  if (!src || (typeof src === 'object' && !(src as any).url)) {
    return null;
  }
  const selectedImage = isSvg
    ? src
    : typeof src !== 'string'
    ? src[ar] || src
    : src;
  const imgSrc =
    typeof selectedImage !== 'string' ? selectedImage?.url : selectedImage;
  const altTitle = typeof src !== 'string' ? src?.alt : alt;

  const content = (
    <>
      {typeof src === 'string' ? (
        <img src={src} alt={altTitle || ''} {...rest} ref={ref as any} />
      ) : isSvg ? (
        <img src={src.url} alt={altTitle || ''} {...rest} ref={ref as any} />
      ) : (
        <Image
          src={imgSrc}
          alt={altTitle || ''}
          layout={'fill' as any}
          loader={imgixLoader}
          sizes={sizes}
          objectFit="cover"
        />
      )}
    </>
  );

  if (isFit) {
    return (
      <FitImage
        aspectRatio={aspectRatio || (RatioMap as any)[ar]}
        style={{ ...containerStyle }}
        {...rest}
      >
        {content}
      </FitImage>
    );
  }

  if (isSvg) {
    return <Svg>{content}</Svg>;
  }

  if (typeof src !== 'string' && selectedImage.base64) {
    return <OverflowHidden>{content}</OverflowHidden>;
  }

  return content;
});
